import { useQuery } from '@tanstack/react-query'
import { obtenerConfiguracion } from './api/configuracion'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router'
import { Encabezado } from './componentes/Encabezado'
import { Estado } from './componentes/Estado'
/*
  CADA PANTALLA VIAJA EN SU PROPIO ARCHIVO, y no todas juntas.
  Antes de esto el navegador descargaba 573 kB antes de pintar la portada:
  dentro iban la administración entera —catálogo, roles, límites, asignaciones—
  y el mostrador, que un alumno no abre nunca. Con `lazyRouteComponent` cada
  ruta se pide cuando se visita.

  LA PORTADA SE QUEDA EN EL PAQUETE PRINCIPAL a propósito: es la primera
  pantalla y pedirla aparte añadiría un viaje de red justo donde más se nota.
  `defaultPreload: 'intent'`, que ya estaba puesto, hace el resto: al pasar el
  puntero o el foco por un enlace, su pantalla empieza a descargarse antes del
  clic, así que la división no se paga en espera.
*/
import { lazyRouteComponent } from '@tanstack/react-router'
import { Inicio } from './rutas/Inicio'

const Espacios = lazyRouteComponent(() => import('./rutas/Espacios'), 'Espacios')
const Disponibilidad = lazyRouteComponent(
  () => import('./rutas/Disponibilidad'),
  'Disponibilidad',
)
const Registro = lazyRouteComponent(() => import('./rutas/Registro'), 'Registro')
const MiCuenta = lazyRouteComponent(() => import('./rutas/MiCuenta'), 'MiCuenta')
const MisReservaciones = lazyRouteComponent(
  () => import('./rutas/MisReservaciones'),
  'MisReservaciones',
)
const Admin = lazyRouteComponent(() => import('./rutas/Admin'), 'Admin')
const MisAlumnos = lazyRouteComponent(() => import('./rutas/MisAlumnos'), 'MisAlumnos')
const Avisos = lazyRouteComponent(() => import('./rutas/Avisos'), 'Avisos')
const Mostrador = lazyRouteComponent(() => import('./rutas/Mostrador'), 'Mostrador')
import { validarBusquedaDisponibilidad } from './rutas/busqueda-disponibilidad'
import { validarBusquedaMiCuenta } from './rutas/busqueda-mi-cuenta'

/**
 * Enrutado por código, no por generación de archivos.
 *
 * Es deliberado: el andamiaje no debe depender de un paso extra de build (el
 * plugin que genera `routeTree.gen.ts`). Cuando el árbol de rutas crezca lo
 * suficiente para justificarlo, se puede migrar sin cambiar los componentes.
 */

const rutaRaiz = createRootRoute({
  component: Disposicion,
  notFoundComponent: NoEncontrado,
})

function Disposicion() {
  // El nombre de la institución sale de la configuración del servidor, no del
  // código (ADR-0004). Ver apps/web/src/api/configuracion.ts.
  const { data: configuracion } = useQuery({
    queryKey: ['configuracion'],
    queryFn: obtenerConfiguracion,
    staleTime: 10 * 60 * 1000,
  })

  return (
    <>
      <a className="salto-contenido" href="#contenido">
        Saltar al contenido
      </a>
      {/*
        EL ARMAZÓN DE TABLERO. A partir de 64rem el encabezado deja de ser una
        franja superior y pasa a ser una COLUMNA lateral fija; el contenido ocupa
        el resto. Por debajo de ese ancho no hay rejilla y todo se apila como
        antes, así que el teléfono conserva su barra compacta con el menú.

        POR QUÉ CAMBIA EL LUGAR Y NO EL MARCADO: el encabezado y el contenido
        siguen siendo los mismos elementos, en el mismo orden del documento. Un
        lector de pantalla y las pruebas ven exactamente el mismo árbol a
        cualquier ancho, que es de donde salen las diferencias imposibles de
        reproducir. Lo único que cambia es dónde los coloca la rejilla.

        La franja superior costaba 161px de alto en escritorio —el 18% de una
        pantalla de 900— repetidos en cada ruta. De lado, ese espacio vuelve al
        contenido y la navegación queda siempre a la vista, sin desplazarse.
      */}
      <div className="armazon">
        <Encabezado />
        <main className="contenido" id="contenido" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <footer className="pie">
        <p>
          {configuracion?.institucion_nombre
            ? `${configuracion.institucion_nombre}. `
            : ''}
          Sistema en desarrollo, en red local y sin publicar.
        </p>
      </footer>
    </>
  )
}

function NoEncontrado() {
  return (
    <Estado
      tono="aviso"
      titulo="Esa dirección no existe."
      detalle="Revisa el enlace o vuelve al inicio desde el menú de arriba."
    />
  )
}

const rutaInicio = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/',
  component: Inicio,
})

const rutaEspacios = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/espacios',
  component: Espacios,
})

/**
 * La fecha y el espacio viajan en la URL para que la vista se pueda recargar y
 * compartir. `validateSearch` es lo que los convierte en parámetros con tipo:
 * la validación vive en `rutas/busqueda-disponibilidad.ts`.
 */
const rutaDisponibilidad = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/disponibilidad',
  validateSearch: validarBusquedaDisponibilidad,
  component: Disponibilidad,
})

/**
 * Las tres rutas que exigen sesión. La comprobación NO vive aquí, en un
 * `beforeLoad`, sino dentro del componente, envuelto en `<Protegida>`: el
 * estado de sesión ya está en una consulta de TanStack Query que el encabezado
 * también usa, y duplicar la lectura en el enrutador significaría pedir
 * `/api/perfil` dos veces y tener dos sitios que pueden discrepar sobre quién
 * eres.
 *
 * `/espacios` y `/disponibilidad` se quedan abiertas a propósito: son de solo
 * lectura y hoy, sin OAuth configurado en el despliegue, protegerlas dejaría la
 * aplicación entera inaccesible.
 */
const rutaRegistro = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/registro',
  component: Registro,
})

/**
 * `validateSearch` aquí no es para compartir la vista: es porque better-auth
 * devuelve el navegador a esta ruta con `?error=…` cuando el inicio de sesión
 * con Google falla. Ver `rutas/busqueda-mi-cuenta.ts`.
 */
const rutaMiCuenta = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/mi-cuenta',
  validateSearch: validarBusquedaMiCuenta,
  component: MiCuenta,
})

/**
 * Protegida como `/mi-cuenta`, y por la misma razón: aquí se ven y se cancelan
 * reservaciones propias. No lleva `validateSearch` porque no tiene parámetros;
 * lo que se está viendo es «lo mío», y eso ya lo determina la sesión.
 */
const rutaMisReservaciones = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/mis-reservaciones',
  component: MisReservaciones,
})

/**
 * Administración. Protegida por SESIÓN y además por ROL, y las dos
 * comprobaciones viven dentro del componente —en `<SoloAdministrador>`— por la
 * misma razón que las tres de arriba: el perfil ya está en una consulta de
 * TanStack Query que el encabezado también usa, y repetir la lectura en un
 * `beforeLoad` significaría pedir `/api/perfil` dos veces y tener dos sitios
 * que pueden discrepar sobre quién eres.
 *
 * Que la ruta EXISTA para cualquiera es deliberado: quien llegue sin el rol ve
 * una explicación de qué es esta sección, no un 404 que le haría pensar que la
 * dirección está mal escrita. La protección real de estos datos está en el API,
 * que responde 403 sin el rol; esta pantalla no es la barrera.
 */
const rutaAdmin = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin',
  component: Admin,
})

/**
 * Lo que un docente ve de lo suyo. Mismo criterio que las de arriba: la ruta
 * existe para cualquiera y el componente explica a quién no le toca.
 *
 * NO cuelga de `/admin` a propósito. Consultar a tus propios alumnos no es
 * administrar, igual que `/mis-reservaciones` no lo es; meterla ahí obligaría a
 * dar permisos de administración a quien solo tiene que mirar su lista.
 */
const rutaMisAlumnos = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/mis-alumnos',
  component: MisAlumnos,
})

/**
 * Los avisos de cada quien y sus preferencias. No exige ningún permiso: recibir
 * avisos es la contraparte de usar el sistema, no una capacidad que se otorgue.
 */
const rutaAvisos = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/avisos',
  component: Avisos,
})

/**
 * El tablero de turno del mostrador. Protegida por SESIÓN y por ROL —dos roles,
 * `mostrador` y `administrador`— con la misma estructura que `/admin`, y por la
 * misma razón: la comprobación vive dentro del componente, en `<SoloMostrador>`,
 * para no leer el perfil dos veces ni tener dos sitios que puedan discrepar
 * sobre quién eres.
 *
 * No lleva `validateSearch` porque no tiene parámetros: lo que se ve es
 * «ahora», y eso no se elige. Tampoco tiene sentido compartir su URL con una
 * fecha, como sí lo tiene `/disponibilidad`.
 */
const rutaMostrador = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/mostrador',
  component: Mostrador,
})

const arbolDeRutas = rutaRaiz.addChildren([
  rutaInicio,
  rutaEspacios,
  rutaDisponibilidad,
  rutaRegistro,
  rutaMiCuenta,
  rutaMisReservaciones,
  rutaMostrador,
  rutaAdmin,
  rutaMisAlumnos,
  rutaAvisos,
])

export const enrutador = createRouter({
  routeTree: arbolDeRutas,
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof enrutador
  }
}
